import React, { useContext, useState } from 'react';
import clsx from 'clsx';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import Layout from '../../common_components/Layout';
import { AppContext } from '../contexts';
import { Checkbox, RadioBtn } from '../components/Inputs';
import {
  AlertError,
  AlertFormSubmitted,
  AlertTrainingTaskCompleted,
} from '../components/Alerts';
import { triggerModalUpdate } from '../../TaskModal';

export default function ModelTraining({ data, isCompleted }) {
  const app = useContext(AppContext);
  const stepsApi = app.APIs.steps;
  const numberOfVariants = 6;
  const variantFieldPrefix = 'variant--';
  const dependentPredictorsPrefix = 'dependent_predictors--';
  const estimatedTimes = app.estimatedTimes.modelTraining;

  const [chosenModel, setChosenModel] = useState(
    data?.model_type ?? 'randomforest',
  ); //  xgboost | randomforest
  const tableData = data['form_data']['parameters'];
  const parameters = tableData[chosenModel].map(({ formName }) => formName);
  const [formState, setFormState] = useState(undefined);

  const handleFormSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.target);
    const variants = {};
    data.forEach((value, key) => {
      if (key.startsWith(variantFieldPrefix)) {
        key = key.replace(variantFieldPrefix, '');
        const [variant, parameter] = key.split('--');
        if (!variants[variant]) {
          variants[variant] = {};
        }
        if (value === 0 || value) {
          variants[variant][parameter] = Number(value);
        }
      }
      if (key.startsWith(dependentPredictorsPrefix)) {
        // add dependant predictors to normal predictors list.
        // we use them only to take advantage of radio buttons,
        // but on the backend they are the same list as the rest
        data.append('predictors', value);
      }
    });
    const notFullyFilledVariants = Object.keys(variants).filter((variant) => {
      for (let param of parameters) {
        if (!variants[variant][param] && variants[variant][param] !== 0) {
          return true;
        }
      }
      return false;
    });
    notFullyFilledVariants.forEach((v) => delete variants[v]);
    if (Object.keys(variants).length === 0) {
      setFormState('invalid-variants');
      return;
    }
    data.append('variants', JSON.stringify(variants));
    setFormState('awaiting-response');
    stepsApi.modelTraining(app.activeProject.id, data).then((projectData) => {
      if (projectData) {
        app.setActiveProject(projectData);
        setFormState('response-ok');
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        triggerModalUpdate({ status: 200 });
      }
    });
  };

  const formWasAlreadySubmitted = Boolean(data['predictors']); // if there is any other data then "form_data" which is used to render the form then it means it was submitted

  return (
    <Layout
      title="Trening Modelu"
      mainContent={
        <>
          {isCompleted && (
            <AlertTrainingTaskCompleted
              mainMessage={'Trening modelu zakończony!'}
              subMessage={
                'Trening modelu zakończył się powodzeniem. Możesz teraz przejść dalej.'
              }
              additionalContent={<ProceedButtons />}
            />
          )}
          {!isCompleted &&
            (formState == 'response-ok' || formWasAlreadySubmitted) && (
              <AlertFormSubmitted
                estimatedTrainingTime={estimatedTimes[chosenModel]}
              />
            )}
          <form
            method="POST"
            className="flex flex-col justify-center items-center"
            onSubmit={handleFormSubmit}
          >
            <fieldset
              className="flex flex-col items-center justify-center"
              disabled={
                formState == 'response-ok' ||
                formState == 'awaiting-response' ||
                formWasAlreadySubmitted
              }
            >
              <h2>Wybierz model</h2>
              <div className="flex gap-4">
                <RadioBtn
                  label="Random Forest (drzew losowych)"
                  defaultChecked={chosenModel == 'randomforest'}
                  onSelect={() => {
                    setChosenModel('randomforest');
                  }}
                  name="model-type"
                  value="randomforest"
                />
                <RadioBtn
                  label="Xgboost (wzmocnienia gradientowego)"
                  defaultChecked={chosenModel == 'xgboost'}
                  onSelect={() => {
                    setChosenModel('xgboost');
                  }}
                  name="model-type"
                  value="xgboost"
                />
              </div>
              <h2>Potwierdź wybór zmiennych do tworzenia modelu</h2>
              <div className="flex flex-wrap justify-center w-3/4 gap-4">
                {data['form_data']?.['variables']?.map((v) => (
                  <Checkbox
                    name="predictors"
                    textClasses="text-black text-md"
                    key={v}
                    value={v}
                    defaultChecked={data?.predictors?.includes(v)}
                  >
                    {v}
                  </Checkbox>
                ))}
              </div>
              <h2>Potwierdź wybór zmiennych jakościowych</h2>
              <div className="flex flex-wrap justify-center w-3/4 gap-16">
                {data['form_data']?.['dependent_variables']?.map(
                  ({ group_members, default_selected }, var_set_index) => (
                    <div className="flex gap-4" key={'var_set' + var_set_index}>
                      {group_members.map((v, i) => {
                        return (
                          <RadioBtn
                            label={v}
                            value={v}
                            key={v}
                            defaultChecked={
                              formWasAlreadySubmitted
                                ? data['predictors'].includes(v)
                                : v == default_selected
                            }
                            name={`${dependentPredictorsPrefix}${var_set_index}`}
                          />
                        );
                      })}
                    </div>
                  ),
                )}
              </div>
              <h2 id="data-split-label">Ustaw podział danych</h2>
              <div className="flex items-center">
                <input
                  type="number"
                  step={0.025}
                  defaultValue={data?.data_split || 0.75}
                  min={0.6}
                  max={0.9}
                  className="input input-bordered w-20 text-black"
                  name="data-split"
                  aria-labelledby="data-split-label"
                  aria-describedby="data-split-description"
                />
                <span className="ml-2" id="data-split-description">
                  (Zakres 0.6-0.9)
                </span>
              </div>
              <p className="w-3/4 text-center">
                Podział danych na zbiór treningowy i testowy. Wartość 0.75
                oznacza, że pierwotny zestaw danych zostanie podzielony na
                zestaw treningowy zawierający 75 % obserwacji. Natomiast zestaw
                testowy stanowić będzie 25 % obserwacji.
              </p>
              <h2>Wybierz parametry strojenia modelu</h2>
              <HiperparametersVariantsTable
                data={tableData[chosenModel]}
                stepData={data}
                numberOfVariants={numberOfVariants}
                variantFieldPrefix={variantFieldPrefix}
              />
              {formState == 'invalid-variants' && (
                <AlertError msg="Wypełnij w pełni przynajmniej jeden wariant!" />
              )}
              <button
                className={clsx(
                  formState == 'response-ok' ||
                    formState == 'awaiting-response' ||
                    formWasAlreadySubmitted
                    ? 'btn btn-disabled btn-lg btn-wide mt-8 opacity-50 cursor-not-allowed'
                    : 'btn btn-success btn-lg btn-wide mt-8',
                )}
                type="submit"
                disabled={
                  formState == 'response-ok' ||
                  formState == 'awaiting-response' ||
                  formWasAlreadySubmitted
                }
              >
                Trenuj
              </button>
            </fieldset>
          </form>
        </>
      }
    ></Layout>
  );
}

function ProceedButtons({}) {
  const app = useContext(AppContext);
  const handleModelEvaluationBtn = () => {
    app.setVisibleStep('modelevaluation');
  };
  const handleNormalizationBtn = () => {
    app.setVisibleStep('normalization');
  };

  return (
    <div className="flex w-full justify-center gap-5">
      <div className="btn btn-info" onClick={handleModelEvaluationBtn}>
        Ocena Modelu
      </div>
      <div className="btn btn-primary" onClick={handleNormalizationBtn}>
        Normalizacja
      </div>
    </div>
  );
}

function HiperparametersVariantsTable({
  data,
  stepData,
  numberOfVariants,
  variantFieldPrefix,
}) {
  const variantsNames = new Array(numberOfVariants)
    .fill(null)
    .map((_, i) => `var${i + 1}`);

  return (
    <div className="overflow-x-auto">
      <table className="table table-md table-zebra table-vertical-divided text-black">
        <thead>
          <tr className="border-gray-200 border-b-2">
            <th aria-label="Nazwa parametru">Parametr</th>
            {variantsNames.map((name) => (
              <th key={name} className="text-lg leading-[18px] text-center">
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((parameter) => (
            <tr key={parameter.name}>
              <th className="text-lg leading-[18px]">
                {parameter.definition ? (
                  <Tippy
                    content={parameter.definition}
                    placement="right"
                    theme="custom-dark"
                  >
                    <span>
                      {parameter.name}{' '}
                      <span className="font-normal break-inside-avoid inline-block">
                        (
                        {getHiperparameterRangeLabel(
                          parameter.minValue,
                          parameter.maxValue,
                        )}
                        )
                      </span>
                    </span>
                  </Tippy>
                ) : (
                  <span>
                    {parameter.name}{' '}
                    <span className="font-normal break-inside-avoid inline-block">
                      (
                      {getHiperparameterRangeLabel(
                        parameter.minValue,
                        parameter.maxValue,
                      )}
                      )
                    </span>
                  </span>
                )}
              </th>
              {variantsNames.map((variant) => (
                <td className="text-lg text-center font-normal" key={variant}>
                  <input
                    type="number"
                    className="input w-full text-center bg-inherit"
                    min={parameter.minValue}
                    max={parameter.maxValue}
                    step={parameter.step}
                    name={`${variantFieldPrefix}${variant}--${parameter.formName}`}
                    defaultValue={
                      stepData?.variants?.[variant]?.[parameter.formName] || ''
                    }
                    aria-label={`${parameter.name} dla ${variant}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function getHiperparameterRangeLabel(minValue, maxValue) {
  if (minValue && maxValue) {
    return `${minValue} - ${maxValue}`;
  } else if (minValue) {
    return ` > ${minValue}`;
  } else if (maxValue) {
    return ` < ${maxValue}`;
  }
}
